import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import React from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import {
  FeatureFlagEnum,
  FormStateEnum,
  ModuleEnum,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {SaveButton} from 'src/design/molecules/SaveButton';
import {FormTransitionBar} from 'src/app/forms/FormTransitionBar';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {OfflineCaptureProvider} from 'src/app/forms/offline/OfflineCaptureContext';
import {FormsOfflineBoundary} from 'src/app/forms/FormsOfflineBoundary';
import {FormHeaderMeta} from 'src/app/forms/FormHeaderMeta';
import {FormReferenceImageSection} from 'src/app/forms/FormReferenceImageSection';
import {DemonstrationContextBanner} from 'src/app/training/DemonstrationContextBanner';
import {parseNumericParam} from 'src/shared/utils/parseNumericParam';
import {SidebarLinkForms} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useAppSelector} from 'src/redux/hooks';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useFormsBreadcrumbs} from 'src/shared/hooks/useFormsBreadcrumbs';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {capitalize} from 'src/shared/utils/formatters';
import {isValidArray} from 'src/utilities/objectUtils';
import {getFormPillConfig} from '../../../../../app/forms/formHelpers';
import {useFormSubmissionEditor} from '../../../../../app/forms/hooks/useFormSubmissionEditor';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';

const EditFormPage: React.FC = () => {
  const navigate = useAppNavigate();
  const {formSubmissionId, formDefinitionId, taskAssignmentId} = useParams();
  const [searchParams] = useSearchParams();

  const extensionTabId = searchParams.get('extensionTabId');
  const targetEntityType = searchParams.get('targetEntityType');
  const targetEntityId = searchParams.get('targetEntityId');

  const {accessToken} = useAppSelector(state => state.auth);

  const {
    form,
    formDefinition,
    formName,
    formApi,
    formMeta,
    formReferenceImage,
    isLoading,
    isSavePending,
    currentFormId,
    stateMachine,
    handleValidateAndSave,
  } = useFormSubmissionEditor({
    formDefinitionId: formDefinitionId!,
    formSubmissionId,
    taskAssignmentId,
    extensionTabId: extensionTabId ?? undefined,
    targetEntityType: targetEntityType ?? undefined,
    targetEntityId: targetEntityId ?? undefined,
  });

  useSetDocumentTitle(capitalize(formName));

  const {breadcrumbs, fallbackTo} = useFormsBreadcrumbs({
    mode: formSubmissionId ? 'form-edit' : 'form-submit',
    formDefinitionId: formDefinition?.id,
    formDefinitionName: formDefinition?.title,
  });
  const {returnTo} = useBackNavigation(fallbackTo);

  const onSubmitted = () =>
    navigate(
      PathUtility.FormSubmission(
        String(formDefinitionId),
        String(currentFormId.current),
        'content'
      ),
      {replace: true}
    );

  const onTransitionComplete = () => {
    const linkedAssignmentId =
      taskAssignmentId ?? form?.metadata?.taskAssignmentId;
    if (linkedAssignmentId) {
      returnTo(
        PathUtility.ViewTaskAssignment(TaskTypeEnum.Form, linkedAssignmentId)
      );
    } else {
      onSubmitted();
    }
  };

  // Queued offline submits have no server id yet — return to the submissions
  // list (offline-capable) rather than a task or submission view that fetches by id.
  const onOfflineQueued = () => {
    returnTo(PathUtility.FormsList(String(formDefinitionId)));
  };

  const pillOptions = getFormPillConfig(
    form?.state ?? FormStateEnum.Draft,
    form?.internalIdentifier
  );

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Forms}>
      {!isLoading ? (
        <Box style={{display: 'flex', flexDirection: 'column', width: '100%'}}>
          <BackBar fallbackTo={fallbackTo} breadcrumbs={breadcrumbs} />
          <PageHeader>
            <PageHeader.Title icon={SidebarLinkForms.icon}>
              {formDefinition ? `${formDefinition?.title}` : 'Form not found'}
            </PageHeader.Title>
            {formSubmissionId && pillOptions.length > 0 && (
              <PageHeader.Badges>
                {pillOptions.map(pill => (
                  <PageHeader.Badge
                    key={pill.text}
                    color={pill.color}
                    variant="light">
                    {pill.text}
                  </PageHeader.Badge>
                ))}
              </PageHeader.Badges>
            )}
            {formSubmissionId && (
              <PageHeader.Meta>
                <FormHeaderMeta
                  isAnonymousSubmission={
                    formDefinition?.metadata?.anonymousSubmission === true
                  }
                  creatorLastName={form?.creator?.lastName}
                  creatorFirstName={form?.creator?.firstName}
                  updatedAt={form?.updatedAt?.toString()}
                />
              </PageHeader.Meta>
            )}
          </PageHeader>
          <DemonstrationContextBanner
            taskAssignmentId={
              parseNumericParam(taskAssignmentId) ??
              form?.metadata?.taskAssignmentId ??
              undefined
            }
          />
          <OfflineCaptureProvider enabled={formSubmissionId === undefined}>
            <ProactForm form={formApi}>
              <ProactFormGrid showValidationSummary />
            </ProactForm>
          </OfflineCaptureProvider>
          <Group align="start">
            {stateMachine && (
              <FormTransitionBar
                preTransitionButtonNodes={[
                  <SaveButton
                    loading={isSavePending}
                    onClick={async () => {
                      const resp = await handleValidateAndSave();
                      if (resp.offlineQueued) {
                        onOfflineQueued();
                      } else if (resp.continue) {
                        onSubmitted();
                      }
                    }}
                    mr="md"
                  />,
                ]}
                onTransitioning={handleValidateAndSave}
                onOfflineQueued={onOfflineQueued}
                module={ModuleEnum.Forms}
                stateMachine={stateMachine}
                stateId={form?.state ?? FormStateEnum.Draft}
                transitionBaseUrl={`form/${currentFormId.current}/transition`}
                onRefreshTransition={onTransitionComplete}
                invalidateTagsOnComplete={['Forms', 'TaskAssignments']}
              />
            )}
          </Group>
          <FormReferenceImageSection
            imageUrl={formReferenceImage}
            accessToken={accessToken}
          />
        </Box>
      ) : (
        <TableBodyRender
          isLoading={isLoading}
          hasData={isValidArray(formMeta) || formSubmissionId === undefined}
        />
      )}
    </ContentWrapper>
  );
};

// Register the routes with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'form',
  path: PathUtility.FormSubmit(':formDefinitionId'),
  element: (
    <FormsOfflineBoundary>
      <EditFormPage />
    </FormsOfflineBoundary>
  ),
  isPrivate: true,
  supportsOffline: true,
});

// The edit page loads getForm plus the version-pinned definition from the
// network, so it cannot function offline — no supportsOffline here.
routeRegistry.registerPageRoute({
  backNavigation: 'form',
  path: PathUtility.FormEdit(':formDefinitionId', ':formSubmissionId'),
  element: (
    <FormsOfflineBoundary>
      <EditFormPage />
    </FormsOfflineBoundary>
  ),
  isPrivate: true,
});

routeRegistry.registerPageRoute({
  backNavigation: 'form',
  path: PathUtility.FormTaskEdit(':formDefinitionId', ':taskAssignmentId'),
  element: (
    <FormsOfflineBoundary>
      <EditFormPage />
    </FormsOfflineBoundary>
  ),
  isPrivate: true,
  supportsOffline: true,
});

export default EditFormPage;
